<template>
    <div>
        <v-expansion-panels
            class="pa-0"
            flat
        >
            <template v-for="(item, index) in items">
                <v-expansion-panel
                    v-if="item.enabled"
                    :key="index"
                >
                    <v-expansion-panel-header class="d-flex justify-start pa-0">
                        <div class="d-flex align-center">
                            <v-icon
                                class="mr-2"
                                size="14"
                            >
                                {{ item.icon }}
                            </v-icon>

                            <span>{{ item.title }}</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                        <template v-if="item.value === 1">
                            <v-radio-group
                                v-model="itemsPerPage"
                                class="pa-0"
                                dense
                            >
                                <v-radio
                                    v-for="(option, itemsIndex) in item.options"
                                    :key="itemsIndex"
                                    :value="option.value"
                                    class="rtl"
                                    label="OMG"
                                    dark
                                    @click="changeItemsPerPage"
                                >
                                    <template #label>
                                        <div class="d-flex justify-end wfull">
                                            <span class="text-subtitle-1 font-weight-bold ltr">{{ option.title }}</span>
                                        </div>
                                    </template>
                                </v-radio>
                            </v-radio-group>
                        </template>

                        <template v-if="item.value === 2">
                            <v-radio-group
                                v-model="fontSize"
                                class="pa-0"
                                dense
                            >
                                <v-radio
                                    v-for="(option, fontsIndex) in item.options"
                                    :key="fontsIndex"
                                    :value="option.value"
                                    class="rtl"
                                    label="OMG"
                                    dark
                                    @click="changeFontSize"
                                >
                                    <template #label>
                                        <div class="d-flex justify-end wfull">
                                            <span class="text-subtitle-1 font-weight-bold">{{ option.title }}</span>
                                        </div>
                                    </template>
                                </v-radio>
                            </v-radio-group>
                        </template>

                        <template v-if="item.value === 3">
                            <v-btn
                                v-for="(option, timerIndex) in item.options"
                                :key="timerIndex"
                                class="timer-btn"
                                text
                                block
                                small
                                @click="changeTimer(option)"
                            >
                                <v-sheet
                                    class="timer-color-indicator"
                                    :style="{ backgroundColor: option.color + ' !important' }"
                                />

                                <span class="text-body-2">{{ option.title }}</span>

                                <span class="text-body-2 ml-auto">{{ option.value }} min</span>
                            </v-btn>
                        </template>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </template>
        </v-expansion-panels>

        <v-dialog
            v-model="timerDialog"
            width="450"
        >
            <v-card
                rounded="lg"
                width="450"
            >
                <v-card-title
                    :class="{
                        'amber--text text--accent-2': modelTitle === $t('kds.drawer.yellow_timer'),
                        'red--text': modelTitle === $t('kds.drawer.red_timer'),
                    }"
                    class="text-h6"
                >
                    {{ modelTitle }}

                    <v-spacer />

                    <v-btn
                        icon
                        @click="timerDialog = false"
                    >
                        <v-icon color="black">far fa-times</v-icon>
                    </v-btn>
                </v-card-title>

                <v-divider />

                <v-card-text class="pa-4 text-center">
                    <v-slide-group
                        v-model="model"
                        center-active
                        show-arrows
                    >
                        <v-slide-item
                            v-for="n in 30"
                            :key="n"
                            v-slot="{ active, toggle }"
                        >
                            <v-card
                                class="ma-1 d-flex justify-center align-center"
                                dark
                                height="40"
                                width="40"
                                :color="active ? 'primary' : 'grey lighten-1'"
                                @click="toggle"
                            >
                                <v-card-text class="text-center text-h5 font-weight-bold pa-0">{{ n }}</v-card-text>
                            </v-card>
                        </v-slide-item>
                    </v-slide-group>

                    <div class="text-h6 font-weight-medium black--text mt-3">
                        {{ model + 1 }} minute{{ model ? 's' : '' }}
                    </div>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        class="text-none font-weight-medium text-h5 black--text"
                        text
                        block
                        @click="setTimer"
                    >
                        {{ $t('general.save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineProps } from 'vue';
import i18n from '@/i18n';

import { useAppStore } from '@/store/useAppStore';
import useVuetify from '@/utilities/useVuetify';
import * as mutationTypes from '@/store/mutationTypes';

import AppStorage from '@/wrappers/AppStorage';

const store = useAppStore();
const { breakpoint } = useVuetify();

const props = defineProps({
    limited: {
        type: Boolean,
        default: true,
    }
})

const itemsPerPage = ref([3, 6]);
const fontSize = ref('medium');
const yellowTimer = ref(5);
const redTimer = ref(7);
const timerDialog = ref(false);
const model = ref(4);
const modelTitle = ref(null);

const isMobile = computed(() => breakpoint.value.sm || breakpoint.value.xs);

const items = computed(() => {
    const items = [{
        title: i18n.t('kds.drawer.tickets_per_page'),
        enabled: true,
        value: 1,
        icon: 'far fa-th',
        options: [
            { title: '1 X 2', value: [1, 2] },
            { title: '1 X 3', value: [1, 3] },
            { title: '1 X 4', value: [1, 4] },
            { title: '1 X 6', value: [1, 6] },
            { title: '2 X 2', value: [2, 2] },
            { title: '2 X 3', value: [2, 3] },
            { title: '2 X 4', value: [2, 4] },
            { title: '2 X 6', value: [2, 6] },
            { title: '3 X 2', value: [3, 2] },
            { title: '3 X 3', value: [3, 3] },
            { title: '3 X 4', value: [3, 4] },
            { title: '3 X 6', value: [3, 6] },
        ],
    }, {
        title: i18n.t('kds.drawer.font_size'),
        enabled: true,
        value: 2,
        icon: 'far fa-font',
        options: [
            { title: i18n.t('kds.drawer.small'), value: isMobile.value ? 'm-small' : 'small' },
            { title: i18n.t('kds.drawer.medium'), value: isMobile.value ? 'm-medium' : 'medium' },
            { title: i18n.t('kds.drawer.large'), value: isMobile.value ? 'm-large' : 'large' },
        ],
    }, {
        title: i18n.t('kds.drawer.timers'),
        enabled: !props.limited,
        value: 3,
        icon: 'far fa-stopwatch',
        options: [
            { title: i18n.t('kds.drawer.yellow_timer'), value: yellowTimer.value, color: 'yellow' },
            { title: i18n.t('kds.drawer.red_timer'), value: redTimer.value, color: 'red' },
        ],
    }];

    if (isMobile.value) {
        items[0].options = [
            { title: '1 X 1', value: [1, 1] },
            { title: '1 X 2', value: [1, 2] },
            { title: '2 X 1', value: [2, 1] },
            { title: '2 X 2', value: [2, 2] },
            { title: '3 X 1', value: [3, 1] },
            { title: '3 X 2', value: [3, 2] },
        ];
    }

    return items;
});

const KDSSettings = computed(() => store.state.kdsSettings);

watch(
    () => isMobile.value,
    (value) => {
        if (value) {
            itemsPerPage.value = [2, 2];
        } else {
            itemsPerPage.value = [2, 4];
        }

        changeItemsPerPage();
    },
);

const changeItemsPerPage = () => {
    setKdsSettings();
};

const changeFontSize = () => {
    setKdsSettings();
};

const changeTimer = (item) => {
    modelTitle.value = item.title;
    model.value = item.value - 1;
    timerDialog.value = true;
};

const setTimer = () => {
    if (modelTitle.value === i18n.t('kds.drawer.yellow_timer')) {
        yellowTimer.value = model.value + 1;

        setKdsSettings();
    } else {
        redTimer.value = model.value + 1;

        setKdsSettings();
    }

    timerDialog.value = false;
};

const setKdsSettings = () => {
    AppStorage.local().setItem(
        'kds_settings',
        JSON.stringify({
            itemsPerPage: itemsPerPage.value,
            fontSize: fontSize.value,
            yellowTimer: yellowTimer.value,
            redTimer: redTimer.value,
        }),
    );

    const kdsSettings = {
        itemsPerPage: itemsPerPage.value,
        fontSize: fontSize.value,
        yellowTimer: yellowTimer.value,
        redTimer: redTimer.value,
    };

    store.commit(mutationTypes.SET_KDS_SETTINGS, { kdsSettings });
};

onMounted(() => {
    itemsPerPage.value = KDSSettings.value.itemsPerPage;
    fontSize.value = KDSSettings.value.fontSize;
    yellowTimer.value = KDSSettings.value.yellowTimer;
    redTimer.value = KDSSettings.value.redTimer;
});
</script>

<style lang="scss" scoped>
::v-deep(.v-expansion-panel-content__wrap) {
    padding: 0px !important;
}

::v-deep(.v-input--selection-controls__input) {
    margin-right: 0 !important;
}

::v-deep(.v-input--selection-controls__input) {
    margin-right: -6px !important;
    z-index: 9999;
}

.timer-btn {
    text-transform: none !important;
    ::v-deep(.v-btn__content) {
        display: flex;
        align-items: center;
    }
}

.timer-color-indicator {
    width: 16px;
    height: 16px;
    border-radius: 4px !important;
    margin-right: 6px;
    margin-bottom: 8px;
}
</style>
